import React, { useEffect, useRef, useState } from "react";
import { sendMergeOtp, confirmMerge } from "@api";
import "../ThirdOnboardScreen/third-onboard-screen.css";
import "./style.css";

/**
 * RestoreGalleryScreen — Path A of the gallery-merge flow (split-cart recovery).
 *
 * Rendered by OnboardScreens as ANOTHER STEP of the onboarding sequence — it
 * reuses the ThirdOnboardScreen shell (.obr-overlay / .obr-card / .obr-heading /
 * .obr-subtext / .obr-submit) so it looks like part of the same flow, not a
 * separate modal. No step counter (it's a conditional insert, not "3/2").
 *
 * Preview stack shows real thumbnails from the batch under a heavy blur —
 * approved: the count already leaks, and at this blur subjects aren't
 * recognizable. Verifying via OTP (sent to the OLD cart's contact) adopts
 * that gallery as the active session.
 */

// The menu's WhatsApp bubble (wtsap.svg outer path, inner handset removed).
const BubbleIcon = () => (
  <svg viewBox="0 0 25.945 26.07" fill="currentColor" aria-hidden="true">
    <path d="M22.165,3.789A12.921,12.921,0,0,0,1.833,19.376L0,26.07l6.848-1.8a12.908,12.908,0,0,0,6.173,1.572h.005A12.924,12.924,0,0,0,22.165,3.789ZM13.027,23.664h0a10.721,10.721,0,0,1-5.465-1.5l-.392-.233L3.1,23l1.085-3.962-.255-.406a10.737,10.737,0,1,1,9.1,5.032Z" fillRule="evenodd" />
  </svg>
);

const EnvelopeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 7l-10 6L2 7" />
  </svg>
);

const RestoreGalleryScreen = ({ data, firstName, onSkip, onAdopted }) => {
  const [step, setStep] = useState("choose"); // choose | otp | working
  const [channel, setChannel] = useState("sms");
  const [code, setCode] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiryTime, setExpiryTime] = useState(0);
  const codeRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (expiryTime <= 0) return;
    const t = setTimeout(() => setExpiryTime((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [expiryTime]);

  const sendCode = async (ch) => {
    setLoading(true);
    setError("");
    try {
      await sendMergeOtp(ch, { source: "onboarding" });
      setChannel(ch);
      setStep("otp");
      setCode(["", "", "", ""]);
      setResendCooldown(60);
      setExpiryTime(300);
      setTimeout(() => codeRefs[0].current?.focus(), 120);
    } catch (err) {
      setError(err.response?.data?.error || "שגיאה בשליחת הקוד");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      const next = ["", "", "", ""];
      for (let i = 0; i < 4; i++) next[i] = digits[i] || "";
      setCode(next);
      setError("");
      codeRefs[Math.min(digits.length, 3)].current?.focus();
      return;
    }
    const digit = value.replace(/\D/g, "");
    setCode((prev) => {
      const n = [...prev];
      n[index] = digit;
      return n;
    });
    setError("");
    if (digit && index < 3) codeRefs[index + 1].current?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeRefs[index - 1].current?.focus();
    }
    if (e.key === "Enter") handleVerify();
  };

  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 4) {
      setError("יש להזין קוד בן 4 ספרות");
      return;
    }
    if (expiryTime <= 0) {
      setError("הקוד פג תוקף, בקשו קוד חדש");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await confirmMerge(fullCode, { source: "onboarding" });
      setStep("working");
      onAdopted(result);
    } catch (err) {
      setError(err.response?.data?.error || "קוד שגוי, נסו שוב");
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    await sendCode(channel);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  const count = data?.imageCount || 0;
  const previews = Array.isArray(data?.previews) ? data.previews.slice(0, 3) : [];

  return (
    <div className="obr-overlay show rgs-overlay">
      <div className="obr-card rgs-card" onClick={(e) => e.stopPropagation()}>
        <div className="obr-ambient"></div>
        <div className="obr-ambient obr-ambient-2"></div>

        <div className="obr-content rgs-content">
          {step === "working" ? (
            <div className="rgs-working">
              <span className="rgs-spinner" />
              <p className="obr-subtext">פותחים את הגלריה שלך…</p>
            </div>
          ) : (
            <>
              {/* blurred real thumbnails from the batch + count badge */}
              <div className="rgs-stack" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={`rgs-ph rgs-ph${i + 1}${previews[i] ? "" : " rgs-ph--placeholder"}`}
                    style={previews[i] ? { backgroundImage: `url(${previews[i]})` } : undefined}
                  />
                ))}
                <span className="rgs-count">{count}</span>
              </div>

              {step === "choose" ? (
                <>
                  <h2 className="obr-heading rgs-heading">
                    {`ברוכים השבים${firstName ? `, ${firstName}` : ""}`} <i className="obr-emoji">😊</i>
                  </h2>
                  <p className="obr-subtext rgs-subtext">
                    נראה שמצאנו <b>{count} תמונות</b> שהעלית בעבר ונוכל להמשיך מאיפה שעצרת
                  </p>

                  <p className="rgs-verify-label">הזדהות קצרה</p>

                  <div className="rgs-opts">
                    {data?.maskedPhone && (
                      <button type="button" className="rgs-opt" disabled={loading} onClick={() => sendCode("sms")}>
                        <span className="rgs-opt-ico"><BubbleIcon /></span>
                        <span className="rgs-opt-txt">
                          קוד ב-SMS
                          <small>{data.maskedPhone}</small>
                        </span>
                      </button>
                    )}
                    {data?.maskedEmail && (
                      <button type="button" className="rgs-opt" disabled={loading} onClick={() => sendCode("email")}>
                        <span className="rgs-opt-ico"><EnvelopeIcon /></span>
                        <span className="rgs-opt-txt">
                          קוד באימייל
                          <small>{data.maskedEmail}</small>
                        </span>
                      </button>
                    )}
                  </div>

                  {error && <p className="rgs-error">{error}</p>}
                </>
              ) : (
                <>
                  <h2 className="obr-heading rgs-heading">הזינו את הקוד</h2>
                  <p className="obr-subtext rgs-subtext">
                    שלחנו קוד בן 4 ספרות ל-<b dir="ltr" style={{ unicodeBidi: "isolate" }}>{channel === "email" ? data?.maskedEmail : data?.maskedPhone}</b>
                  </p>

                  <div className="rgs-code-row" dir="ltr">
                    {code.map((digit, i) => (
                      <input
                        key={i}
                        ref={codeRefs[i]}
                        type="text"
                        inputMode="numeric"
                        autoComplete={i === 0 ? "one-time-code" : "off"}
                        className={`rgs-code-input${digit ? " filled" : ""}`}
                        value={digit}
                        onChange={(e) => handleCodeChange(i, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(i, e)}
                        maxLength={i === 0 ? 4 : 1}
                      />
                    ))}
                  </div>

                  {expiryTime > 0 ? (
                    <p className="rgs-timer">הקוד תקף ל-{formatTime(expiryTime)}</p>
                  ) : (
                    <p className="rgs-timer rgs-timer--expired">הקוד פג תוקף</p>
                  )}

                  {error && <p className="rgs-error">{error}</p>}

                  <button
                    type="button"
                    className="obr-submit"
                    onClick={handleVerify}
                    disabled={loading || code.join("").length !== 4}
                  >
                    {loading ? <span className="obr-submit-spinner" /> : "להמשיך מאיפה שעצרתם"}
                  </button>

                  <div className="rgs-links">
                    <button type="button" className="rgs-link" onClick={handleResend} disabled={resendCooldown > 0}>
                      {resendCooldown > 0 ? `שליחה חוזרת בעוד ${resendCooldown} שניות` : "שלח קוד חדש"}
                    </button>
                    <button type="button" className="rgs-link" onClick={() => { setStep("choose"); setError(""); }}>
                      חזרה
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* footer rendered on every step (empty on otp/working) so the sheet height never jumps */}
        <div className="obr-footer rgs-footer">
          {step === "choose" && (
            <p className="obr-skip" onClick={onSkip}>התחלה חדשה בלי התמונות</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestoreGalleryScreen;
